import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Flag, MessageSquare, User, FileText, ExternalLink, Trash2, Check, X, RotateCcw } from 'lucide-react';
import { getApiBaseUrl } from '../../config/api';
import { fetchReports, resolveReport } from '../../utils/moderation';
import { getAuthorDisplayName } from '../../utils/displayName';
import { resolveAvatarUrl, handleAvatarError } from '../../utils/profilePicUrl';
import './ReportsQueue.css';

const API_URL = getApiBaseUrl();

const STATUS_TABS = [
  { id: 'open', label: 'Open' },
  { id: 'actioned', label: 'Actioned' },
  { id: 'dismissed', label: 'Dismissed' },
  { id: 'all', label: 'All' },
];

const TYPE_META = {
  post: { label: 'Post', Icon: FileText },
  reply: { label: 'Comment', Icon: MessageSquare },
  user: { label: 'Member', Icon: User },
};

function formatWhen(value) {
  if (!value) return '';
  const d = new Date(String(value).replace(' ', 'T'));
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
}

/** Admin moderation queue: reports members filed on posts, comments, and members. */
export default function ReportsQueue({ user }) {
  const [status, setStatus] = useState('open');
  const [reports, setReports] = useState([]);
  const [openCount, setOpenCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    const data = await fetchReports(user.id, status);
    if (data.success) {
      setReports(data.reports || []);
      setOpenCount(data.open_count || 0);
      setError('');
    } else {
      setError(data.message || 'Could not load reports.');
    }
    setLoading(false);
  }, [user?.id, status]);

  useEffect(() => {
    load();
  }, [load]);

  const setReportStatus = async (report, nextStatus) => {
    setBusyId(report.id);
    const result = await resolveReport(user.id, report.id, nextStatus);
    setBusyId(null);
    if (!result.success) {
      alert(result.message || 'Could not update the report.');
      return;
    }
    load();
  };

  const removeContent = async (report) => {
    const label = report.target_type === 'reply' ? 'comment' : 'post';
    if (!window.confirm(`Delete this ${label} permanently? This cannot be undone.`)) return;

    setBusyId(report.id);
    try {
      const formData = new FormData();
      let endpoint;
      if (report.target_type === 'post') {
        endpoint = 'delete_post.php';
        formData.append('post_id', report.target_id);
        formData.append('user_id', user.id);
      } else {
        endpoint = 'delete_reply.php';
        formData.append('reply_id', report.target_id);
        formData.append('user_id', user.id);
      }
      const res = await fetch(`${API_URL}/${endpoint}`, { method: 'POST', body: formData });
      const data = await res.json();
      // Already-deleted content still counts as handled.
      if (!data.success && !/not found|already deleted/i.test(data.message || '')) {
        alert(data.message || `Could not delete the ${label}.`);
        return;
      }
      await resolveReport(user.id, report.id, 'actioned', `Removed ${label}`);
      load();
    } catch (err) {
      console.error(err);
      alert('Network error. Please try again.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section className="rq">
      <header className="rq-header">
        <div>
          <h2 className="rq-title">
            <Flag size={20} strokeWidth={2.2} aria-hidden="true" /> Reports
          </h2>
          <p className="rq-subtitle">
            {openCount === 0
              ? 'No open reports. Nice and quiet.'
              : `${openCount} open report${openCount === 1 ? '' : 's'} waiting for review`}
          </p>
        </div>
        <div className="rq-tabs" role="tablist">
          {STATUS_TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={status === t.id}
              className={`rq-tab${status === t.id ? ' rq-tab--active' : ''}`}
              onClick={() => setStatus(t.id)}
            >
              {t.label}
              {t.id === 'open' && openCount > 0 && <span className="rq-badge">{openCount}</span>}
            </button>
          ))}
        </div>
      </header>

      {error && <p className="rq-error">{error}</p>}
      {loading ? (
        <p className="rq-empty">Loading reports…</p>
      ) : reports.length === 0 ? (
        <p className="rq-empty">No {status === 'all' ? '' : status} reports.</p>
      ) : (
        <ul className="rq-list">
          {reports.map((r) => {
            const meta = TYPE_META[r.target_type] || TYPE_META.post;
            const TypeIcon = meta.Icon;
            const targetName = getAuthorDisplayName(r.target_user) || 'Unknown member';
            const reporterName = getAuthorDisplayName(r.reporter) || 'A member';
            const viewLink =
              r.target_type === 'user'
                ? `/profile/${r.target_user.id}`
                : r.post_id
                  ? `/home/post/${r.post_id}`
                  : null;
            const busy = busyId === r.id;

            return (
              <li key={r.id} className={`rq-item rq-item--${r.status}`}>
                <div className="rq-item-top">
                  <span className={`rq-type rq-type--${r.target_type}`}>
                    <TypeIcon size={14} strokeWidth={2.2} aria-hidden="true" /> {meta.label}
                  </span>
                  <span className="rq-reason">{r.reason_label}</span>
                  {r.open_report_count > 1 && (
                    <span className="rq-count" title="Open reports on this same item">
                      {r.open_report_count} reports
                    </span>
                  )}
                  <span className="rq-when">{formatWhen(r.created_at)}</span>
                </div>

                <div className="rq-target">
                  <img
                    src={resolveAvatarUrl(r.target_user, API_URL)}
                    onError={handleAvatarError}
                    alt=""
                    className="rq-avatar"
                  />
                  <div className="rq-target-text">
                    <Link to={`/profile/${r.target_user.id}`} className="rq-target-name">
                      {targetName}
                    </Link>
                    {r.target_type !== 'user' && (
                      r.target_exists ? (
                        <p className="rq-preview">{r.preview || <em>(no text — media only)</em>}</p>
                      ) : (
                        <p className="rq-preview rq-preview--gone">This content has already been deleted.</p>
                      )
                    )}
                  </div>
                </div>

                {r.details && <blockquote className="rq-details">“{r.details}”</blockquote>}
                <p className="rq-reporter">Reported by {reporterName}</p>
                {r.status !== 'open' && r.admin_note && (
                  <p className="rq-reporter">Admin note: {r.admin_note}</p>
                )}

                <div className="rq-actions">
                  {viewLink && r.target_exists && (
                    <Link to={viewLink} className="rq-btn" target="_blank" rel="noopener noreferrer">
                      <ExternalLink size={14} aria-hidden="true" /> View
                    </Link>
                  )}
                  {r.status === 'open' ? (
                    <>
                      {r.target_type !== 'user' && r.target_exists && (
                        <button
                          type="button"
                          className="rq-btn rq-btn--danger"
                          disabled={busy}
                          onClick={() => removeContent(r)}
                        >
                          <Trash2 size={14} aria-hidden="true" /> Remove {r.target_type === 'reply' ? 'comment' : 'post'}
                        </button>
                      )}
                      <button
                        type="button"
                        className="rq-btn rq-btn--primary"
                        disabled={busy}
                        onClick={() => setReportStatus(r, 'actioned')}
                        title="Mark as handled (e.g. you warned or suspended the member)"
                      >
                        <Check size={14} aria-hidden="true" /> Mark handled
                      </button>
                      <button
                        type="button"
                        className="rq-btn"
                        disabled={busy}
                        onClick={() => setReportStatus(r, 'dismissed')}
                      >
                        <X size={14} aria-hidden="true" /> Dismiss
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      className="rq-btn"
                      disabled={busy}
                      onClick={() => setReportStatus(r, 'open')}
                    >
                      <RotateCcw size={14} aria-hidden="true" /> Reopen
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
